import { useState } from "react";
import { Link } from "react-router-dom";
import { FileDown, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useLang } from "../lang";
import { useDatasheet, postLead } from "./DatasheetContext";

const TYPES = [
  { id: "privato", it: "Privato", en: "Homeowner" },
  { id: "professionista", it: "Professionista", en: "Professional" },
  { id: "impresa", it: "Impresa", en: "Contractor" },
  { id: "serramentista", it: "Serramentista", en: "Window fabricator" },
];

const input = "w-full rounded-xl border border-[#d2d2d7] bg-white px-4 py-3 text-[15px] text-[#1d1d1f] outline-none transition-colors focus:border-[#1D3F94]";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block mb-1.5 text-[12px] uppercase tracking-wider text-[#6e6e73]">{label}</span>
      {children}
    </label>
  );
}

function LeadForm({ sheet, onDone }) {
  const { lang } = useLang();
  const { saveLead } = useDatasheet();
  const it = lang === "it";
  const [f, setF] = useState({ name: "", email: "", phone: "", client_type: "", privacy_consent: false });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.client_type) return setErr(it ? "Seleziona il tipo di cliente." : "Select your customer type.");
    if (!f.privacy_consent) return setErr(it ? "Per proseguire accetta l'informativa privacy." : "Please accept the privacy policy.");
    setBusy(true);
    setErr("");
    const lead = { ...f, name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim() || null };
    try {
      const r = await postLead(lead, sheet, lang);
      if (!r.ok) throw new Error();
      saveLead(lead);
      onDone();
    } catch {
      setErr(it ? "Invio non riuscito. Controlla i dati e riprova." : "Sending failed. Check your details and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="datasheet-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={it ? "Nome e cognome" : "Full name"}>
          <input required minLength={2} value={f.name} onChange={set("name")} className={input} data-testid="datasheet-name" />
        </Field>
        <Field label="Email">
          <input required type="email" value={f.email} onChange={set("email")} className={input} data-testid="datasheet-email" />
        </Field>
        <Field label={it ? "Telefono (facoltativo)" : "Phone (optional)"}>
          <input type="tel" value={f.phone} onChange={set("phone")} className={input} data-testid="datasheet-phone" />
        </Field>
        <Field label={it ? "Tipo di cliente" : "Customer type"}>
          <select value={f.client_type} onChange={set("client_type")} className={input} data-testid="datasheet-type">
            <option value="">{it ? "Seleziona…" : "Select…"}</option>
            {TYPES.map((t) => <option key={t.id} value={t.id}>{t[lang]}</option>)}
          </select>
        </Field>
      </div>
      <label className="flex items-start gap-3 text-[13px] text-[#1d1d1f]/80 leading-snug cursor-pointer">
        <input type="checkbox" checked={f.privacy_consent} onChange={set("privacy_consent")} className="mt-0.5 w-4 h-4 accent-[#1D3F94]" data-testid="datasheet-privacy" />
        <span>
          {it ? "Ho letto l'" : "I have read the "}
          <Link to="/privacy" target="_blank" className="text-[#1D3F94] underline">{it ? "informativa privacy" : "privacy policy"}</Link>
          {it ? " e acconsento al trattamento dei miei dati per ricevere la documentazione tecnica ed essere ricontattato." : " and consent to the processing of my data to receive technical documentation and be contacted."}
        </span>
      </label>
      {err && <p className="text-[13px] text-red-600" data-testid="datasheet-error">{err}</p>}
      <button type="submit" disabled={busy} className="btn-pill btn-primary w-full justify-center disabled:opacity-60" data-testid="datasheet-submit">
        {busy ? (it ? "Invio…" : "Sending…") : (it ? "Sblocca le schede tecniche" : "Unlock technical datasheets")}
      </button>
    </form>
  );
}

export default function DatasheetDialog({ sheet, onClose }) {
  const { lang } = useLang();
  const it = lang === "it";
  const [done, setDone] = useState(false);
  const close = () => { setDone(false); onClose(); };

  return (
    <Dialog open={!!sheet} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-[560px] rounded-3xl p-7 md:p-9" data-testid="datasheet-dialog">
        <DialogHeader className="text-left">
          <div className="w-11 h-11 rounded-full bg-[#f5f5f7] text-[#1D3F94] flex items-center justify-center mb-3">
            {done ? <FileDown size={20} /> : <Lock size={18} />}
          </div>
          <DialogTitle className="text-[22px] md:text-[26px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">
            {done ? (it ? "Schede tecniche sbloccate" : "Datasheets unlocked") : (it ? "Scarica la scheda tecnica" : "Download the datasheet")}
          </DialogTitle>
          <DialogDescription className="text-[15px] text-[#6e6e73] leading-relaxed">
            {done
              ? (it ? "Grazie. Da questo dispositivo potrai scaricare tutte le schede senza inserire di nuovo i dati." : "Thank you. From this device you can download every datasheet without entering your details again.")
              : (it ? `Lascia i tuoi contatti per accedere alla documentazione tecnica di ${sheet?.name || ""}. Ti chiediamo i dati una sola volta.` : `Leave your details to access the technical documentation for ${sheet?.name || ""}. We only ask once.`)}
          </DialogDescription>
        </DialogHeader>
        {sheet && (done ? (
          <a href={sheet.url} target="_blank" rel="noopener noreferrer" onClick={close} className="btn-pill btn-primary w-full justify-center mt-2" data-testid="datasheet-download">
            {it ? `Scarica ${sheet.name}` : `Download ${sheet.name}`} <span className="chev">↓</span>
          </a>
        ) : (
          <LeadForm sheet={sheet} onDone={() => setDone(true)} />
        ))}
      </DialogContent>
    </Dialog>
  );
}
